/**
 * Date and Date-Time picker (piece 3): the native date and datetime-local
 * inputs with the plan's wrapper. The picker states which zone it shows
 * wherever the viewer's zone differs from the record's, and a range is two
 * natives with the end bounded by the start.
 */
import { isCalendarDay } from '../zonedTime';
/*
 * 🔨 There is no `viewerTimeZone()` here any more. The viewer's zone is their
 * own `display.timeZone` setting, read from the frame, and a control that asks
 * the browser instead renders a clock nobody chose (decision 0261).
 */
/** The note is drawn only where the record names a zone and it is not the viewer's. */
export function zoneNoteNeeded(viewerZone: string, recordZone: string | null | undefined): boolean {
  return !!recordZone && recordZone !== viewerZone;
}

export function isIsoDateTimeLocal(text: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(text) && isCalendarDay(text.slice(0, 10));
}

/**
 * The end input of a range carries min set to the start. 💰 That attribute is
 * the affordance, not the guard: a desktop picker greys out the earlier dates so
 * nobody can pick one, while on an iPhone the same attribute only marks the
 * field invalid and something has to read that. `rangeIsOrdered` is what reads
 * it (found while building, 2026-09-08).
 */
export function rangeEndMin(start: string): string | undefined {
  return start || undefined;
}

/**
 * Both halves of a range compared on the same shape, so a `datetime-local` with
 * seconds and one without still order correctly. An empty half orders with
 * anything: a range half-filled is not yet wrong.
 */
function comparable(value: string): string {
  if (!value) return '';
  const [date, time] = value.split('T');
  if (time === undefined) return date!;
  const [hours = '00', minutes = '00', seconds = '00'] = time.split(':');
  return `${date}T${hours}:${minutes}:${seconds}`;
}

/** 🔨 The range's own guard: an end before its start is refused whatever the browser allowed. */
export function rangeIsOrdered(start: string, end: string): boolean {
  if (!start || !end) return true;
  return comparable(end) >= comparable(start);
}

export function withinBounds(value: string, min?: string, max?: string): boolean {
  if (min && value < min) return false;
  if (max && value > max) return false;
  return true;
}
